/**
 * Cliente HTTP das certificações pendentes e da assinatura (E52.F3, tela T3).
 *
 * Espelha os contratos do backend (`certificacao_fila.py` e
 * `certificacao_assinatura.py`). Nada é recalculado aqui: prazo,
 * atraso, "vence hoje" e os três indicadores do topo chegam prontos do
 * servidor (BR-10 — nenhum número nasce na UI).
 *
 * O erro é DISCRIMINADO por razão estável porque a tela precisa dizer coisas
 * diferentes: 409 de disputa ("outro certificador está com este item") não é a
 * mesma frase de 422 de checklist ("a revisão precisa estar completa"), e um
 * "erro genérico" nos dois casos esconderia justamente a informação que
 * resolve a situação para quem está na tela.
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';

import type { Modalidade } from '@/lib/cases';

export type StatusCertificacao = 'requerida' | 'em_revisao' | 'laudo_entregue';
export type AmbienteRequisicao = 'escritorio' | 'banco';
export type ResultadoExameCcb = 'favoravel' | 'inconclusivo' | 'desfavoravel' | 'em_exame';

export interface CertificacaoPendente {
  requisicao_id: string;
  case_id: string;
  case_display_code: string;
  source_document_id: string;
  ccb_number: string | null;
  escritorio_nome: string | null;
  resultado_exame: ResultadoExameCcb;
  /** E60.F3 — o caso furou a ordem do prazo; a tela precisa dizer isso. */
  prioritario?: boolean;
  /**
   * E74.F3 (ADR-0075 §8): o checklist tem os MESMOS três pontos nas duas
   * modalidades — conferi a metodologia, as evidências, o texto. Só o **rótulo**
   * do segundo muda: no eletrônico não há comparativo de traço a validar.
   */
  modalidade?: Modalidade;
  requerida_por: string;
  ambiente: AmbienteRequisicao;
  producao_origem: string | null;
  status: StatusCertificacao;
  requested_at: string;
  sla_due_at: string;
  atrasado: boolean;
  vence_hoje: boolean;
}

export interface CertificacaoIndicadores {
  na_fila: number;
  vencem_hoje: number;
  assinados_hoje: number;
}

export interface CertificacoesPendentes {
  itens: CertificacaoPendente[];
  indicadores: CertificacaoIndicadores;
}

export interface ChecklistRevisao {
  metodologia: boolean;
  comparativos: boolean;
  texto: boolean;
}

export interface RequisicaoAssumida {
  requisicao_id: string;
  source_document_id: string;
  status: 'em_revisao';
  certificador_email: string;
  assumido_em: string;
}

export interface AssinaturaLaudo {
  id: string;
  source_document_id: string;
  certificador_email: string;
  certificador_nome: string | null;
  certificador_registro: string | null;
  mesmo_autor_producao: boolean;
  documento_sha256: string;
  padrao: 'interna_registrada' | 'icp_brasil';
  assinado_em: string;
  /** E147.F2 (ADR-0101 §2.6): o pedido repetido do mesmo certificador recebeu a assinatura que já
   * estava gravada — nada foi assinado de novo. */
  reconciliada?: boolean;
}

export type CertificacaoErrorReason =
  | 'disputada'
  | 'nao_assumida'
  | 'revisao_incompleta'
  | 'ja_assinado'
  | 'laudo_nao_pronto'
  | 'saidas_nao_revisadas'
  | 'icp_obrigatoria'
  | 'assinatura_nao_conferida'
  | 'certificado_divergente'
  | 'nao_encontrado'
  | 'sem_permissao'
  | 'rede'
  | 'desconhecido';

export class CertificacaoError extends Error {
  public readonly reason: CertificacaoErrorReason;

  constructor(reason: CertificacaoErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'CertificacaoError';
  }
}

/** O status HTTP sem código conhecido → razão. 502–504 (E147.F3): o intermediário desistiu, e o
 * servidor pode ter concluído. */
const STATUS_TO_REASON: Readonly<Record<number, CertificacaoErrorReason>> = {
  401: 'sem_permissao',
  403: 'sem_permissao',
  404: 'nao_encontrado',
  409: 'ja_assinado',
  422: 'revisao_incompleta',
  502: 'rede',
  503: 'rede',
  504: 'rede',
};

/** `detail` do backend → razão. É o código que distingue os dois 409 e os dois 422. */
const DETAIL_TO_REASON: Record<string, CertificacaoErrorReason> = {
  CERTIFICACAO_DISPUTADA: 'disputada',
  REQUISICAO_NAO_ASSUMIDA: 'nao_assumida',
  LAUDO_JA_ASSINADO: 'ja_assinado',
  REVISAO_INCOMPLETA: 'revisao_incompleta',
  LAUDO_NAO_PRONTO: 'laudo_nao_pronto',
  SAIDAS_NAO_REVISADAS: 'saidas_nao_revisadas',
  ASSINATURA_ICP_OBRIGATORIA: 'icp_obrigatoria',
  ASSINATURA_NAO_CONFERIDA: 'assinatura_nao_conferida',
  LAUDO_CERTIFICADO_DIVERGENTE: 'certificado_divergente',
};

/** A resposta que não chegou (E147.F3): a rede caiu ou o tempo esgotou. O servidor pode ter
 * concluído — a tela relê antes de oferecer a ação de novo. */
const SEM_RESPOSTA_CODIGO: ReadonlySet<string> = new Set([
  'ERR_NETWORK',
  'ECONNABORTED',
  'ETIMEDOUT',
]);

function detailCode(err: AxiosError): string | null {
  const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  return typeof detail === 'string' ? detail : null;
}

function reasonFor(err: AxiosError): CertificacaoErrorReason {
  const code = detailCode(err);
  if (code !== null) {
    const byDetail = DETAIL_TO_REASON[code];
    if (byDetail !== undefined) return byDetail;
  }
  const httpStatus = err.response?.status;
  const byStatus = httpStatus === undefined ? undefined : STATUS_TO_REASON[httpStatus];
  if (byStatus !== undefined) return byStatus;
  if (err.code !== undefined && SEM_RESPOSTA_CODIGO.has(err.code)) return 'rede';
  return 'desconhecido';
}

function classify(err: unknown): CertificacaoError {
  if (!axios.isAxiosError(err)) return new CertificacaoError('desconhecido');
  return new CertificacaoError(reasonFor(err as AxiosError));
}

export async function getPendentes(client: AxiosInstance): Promise<CertificacoesPendentes> {
  try {
    // Caminho do contrato do servidor (E52.F1), não texto de tela.
    const { data } = await client.get<CertificacoesPendentes>(
      '/certificacao/fila', // ux-neutrality:allow contrato-servidor
    );
    return data;
  } catch (err) {
    throw classify(err);
  }
}

export async function assumirRequisicao(
  client: AxiosInstance,
  requisicaoId: string,
): Promise<RequisicaoAssumida> {
  try {
    const { data } = await client.post<RequisicaoAssumida>(
      `/certificacao/requisicoes/${requisicaoId}/assumir`,
    );
    return data;
  } catch (err) {
    throw classify(err);
  }
}

export async function assinarLaudo(
  client: AxiosInstance,
  requisicaoId: string,
  checklist: ChecklistRevisao,
): Promise<AssinaturaLaudo> {
  try {
    const { data } = await client.post<AssinaturaLaudo>(
      `/certificacao/requisicoes/${requisicaoId}/assinar`,
      { checklist },
    );
    return data;
  } catch (err) {
    throw classify(err);
  }
}
